import React, { useMemo } from "react";
import { ArrowLeft, Check, Lock, Mic, Monitor, Smartphone, Video, Wifi } from "lucide-react";

interface SetupCompleteScreenProps {
  mainCameraStream?: MediaStream | null;
  mobileCameraStream?: MediaStream | null;
  audioWorking?: boolean;
  cameraWorking?: boolean;
  screenShareActive?: boolean;
  mobileConnected?: boolean;
  networkStable?: boolean;
  onStartInterview: () => void;
  onExit?: () => void;
  footer?: React.ReactNode;
}

const SetupCompleteScreen: React.FC<SetupCompleteScreenProps> = ({
  mainCameraStream,
  mobileCameraStream,
  audioWorking = true,
  cameraWorking = true,
  screenShareActive = true,
  mobileConnected = true,
  networkStable = true,
  onStartInterview,
  onExit,
  footer,
}) => {
  const allChecksComplete = useMemo(
    () => audioWorking && cameraWorking && screenShareActive && mobileConnected && networkStable,
    [audioWorking, cameraWorking, screenShareActive, mobileConnected, networkStable],
  );

  return (
    <div className="flex w-full flex-col">
      {onExit && (
        <button
          onClick={onExit}
          className="mb-8 flex items-center gap-3 self-start text-[#676A6C] transition-colors hover:text-[#000]"
          aria-label="Exit Interview"
          type="button"
        >
          <ArrowLeft className="h-[11px] w-[11px]" strokeWidth={1.4} />
          <span className="text-base leading-[24px]">Exit Interview</span>
        </button>
      )}

      <div className="mb-8 flex w-full flex-col items-center gap-3">
        <h2 className="text-[24px] font-ubuntu-sans font-semibold leading-[33px] text-[#202326]">You're All Set!</h2>
        <p className="text-sm leading-[21px] text-[#686B6D]">All systems verified and ready for your interview</p>
      </div>

      <div className="w-full">
        <div className="flex w-full gap-6">
          <div className="flex flex-1 gap-4">
            <div className="relative h-[400px] flex-1 overflow-hidden rounded-[15px] border-[3px] border-[#364153] bg-black">
              <div className="absolute left-[18px] top-[18px] z-10 flex items-center gap-2.5 rounded-lg bg-[#3A3A3C] px-4 py-2.5">
                <span className="text-xs font-normal leading-4 text-white">Main Camera</span>
                <div className="h-2.5 w-2.5 rounded-full bg-[#00C950]" />
              </div>

              {mainCameraStream ? (
                <video
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full object-cover"
                  style={{ transform: "scaleX(-1)" }}
                  ref={(video) => {
                    if (video && mainCameraStream) video.srcObject = mainCameraStream;
                  }}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-white">
                  <Video className="h-12 w-12 opacity-50" />
                </div>
              )}
            </div>

            <div className="relative h-[400px] w-[267px] overflow-hidden rounded-[15px] border-[0.98px] border-[#364153] bg-black">
              <div className="absolute left-0 right-0 top-[12px] z-10 mx-[12px] flex items-center gap-2 rounded bg-black/70 px-3 py-2">
                <span className="text-xs font-normal leading-4 text-white">
                  Mobile Device
                  <br />
                  (Workspace Camera)
                </span>
                <div className="ml-auto h-2 w-2 rounded-full bg-[#00C950]" />
              </div>

              {mobileCameraStream ? (
                <div className="h-full w-full overflow-hidden">
                  <video
                    autoPlay
                    playsInline
                    muted
                    className="h-[167%] w-full -mt-[67%] object-cover"
                    ref={(video) => {
                      if (video && mobileCameraStream) video.srcObject = mobileCameraStream;
                    }}
                  />
                </div>
              ) : (
                <div className="flex h-full w-full items-center justify-center text-white">
                  <Smartphone className="h-12 w-12 opacity-50" />
                </div>
              )}
            </div>
          </div>

          <div className="flex w-[300px] flex-shrink-0 flex-col gap-4">
            <div className="flex flex-col gap-3 rounded-[10px] border border-[#E5EAEC] bg-white p-5">
              <div className="flex items-center gap-2">
                <h3 className="flex-1 text-base font-ubuntu-sans font-medium text-[#001630]">System Check Complete</h3>
                <Check className="h-5 w-5 text-[#00A63E]" />
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Mic className={`h-5 w-5 flex-shrink-0 ${audioWorking ? "text-[#00A63E]" : "text-gray-400"}`} />
                  <span
                    className={`text-sm font-normal leading-5 ${audioWorking ? "text-[#364153]" : "text-gray-400"}`}
                  >
                    Audio working
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Video className={`h-5 w-5 flex-shrink-0 ${cameraWorking ? "text-[#00A63E]" : "text-gray-400"}`} />
                  <span
                    className={`text-sm font-normal leading-5 ${cameraWorking ? "text-[#364153]" : "text-gray-400"}`}
                  >
                    Main camera working
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Monitor
                    className={`h-5 w-5 flex-shrink-0 ${screenShareActive ? "text-[#00A63E]" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-sm font-normal leading-5 ${screenShareActive ? "text-[#364153]" : "text-gray-400"}`}
                  >
                    Screen sharing active
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Smartphone
                    className={`h-5 w-5 flex-shrink-0 ${mobileConnected ? "text-[#00A63E]" : "text-gray-400"}`}
                  />
                  <span
                    className={`text-sm font-normal leading-5 ${mobileConnected ? "text-[#364153]" : "text-gray-400"}`}
                  >
                    Mobile Device Connected
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Wifi className={`h-5 w-5 flex-shrink-0 ${networkStable ? "text-[#00A63E]" : "text-gray-400"}`} />
                  <span
                    className={`text-sm font-normal leading-5 ${networkStable ? "text-[#364153]" : "text-gray-400"}`}
                  >
                    Network is Stable
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 rounded-[10px] border border-[#BEDBFF] bg-[#EFF6FF] p-4">
              <Lock className="h-[18px] w-[23px] flex-shrink-0 text-[#1559FC]" />
              <div className="flex flex-col gap-1">
                <h4 className="text-sm font-ubuntu-sans font-semibold leading-6 text-[#101828]">Your privacy is protected</h4>
                <p className="text-[13px] font-normal leading-[22px] text-[#68696B]">
                  Recording begins only during interview questions and stops automatically when complete
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          {footer ? (
            footer
          ) : (
            <button
              onClick={onStartInterview}
              disabled={!allChecksComplete}
              className={`rounded-lg px-20 py-4 text-base font-medium transition-all ${
                allChecksComplete ? "bg-[#030213] text-white hover:bg-[#030213]/90" : "cursor-not-allowed bg-gray-300 text-gray-500"
              }`}
              type="button"
            >
              Start Interview Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupCompleteScreen;

